import React, { useState } from 'react';

function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: 'General Inquiry',
    message: ''
  });
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const serviceTypes = [
    'General Inquiry',
    'Aquatic Vegetation Management',
    'Fountain/Aerator Service',
    'Algae & Muck Control',
    'Water Quality Testing',
    'Storm Water Maintenance'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = () => {
    const newErrors = [];

    if (!formData.name.trim()) {
      newErrors.push("Name can't be blank");
    }

    if (!formData.email.trim()) {
      newErrors.push("Email can't be blank");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.push('Email is invalid');
    }

    if (!formData.phone.trim()) {
      newErrors.push("Phone can't be blank");
    }

    if (!formData.message.trim()) {
      newErrors.push("Message can't be blank");
    } else if (formData.message.trim().length < 10) {
      newErrors.push('Message is too short (minimum is 10 characters)');
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await fetch('/api/v1/contact_inquiries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contact_inquiry: formData })
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: 'General Inquiry',
          message: ''
        });
      } else {
        setErrors(data.errors || ['An error occurred. Please try again.']);
      }
    } catch (error) {
      setErrors(['Unable to submit form. Please try again or call us directly.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="contact" className="bg-white py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-nature-500">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-900 mb-4">
                Thank You!
              </h2>
              <p className="text-lg text-brand-700 mb-6">
                Your message has been sent successfully. Joe will get back to you as soon as possible, typically within 24 hours.
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-brand-700 mb-2">Need immediate assistance?</p>
                <a
                  href="tel:+17272773366"
                  className="inline-flex items-center justify-center bg-water-600 hover:bg-water-700 text-white font-bold py-3 px-6 rounded-lg transition-colors min-h-[48px]"
                >
                  <span className="mr-2">📞</span>
                  Call Joe: (727) 277-3366
                </a>
              </div>
              <button
                onClick={() => setIsSuccess(false)}
                className="text-water-600 hover:text-water-700 font-medium"
              >
                Send another message
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="bg-white py-8 md:py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-900 text-center mb-4">
          Contact Us
        </h2>
        <p className="text-xl text-brand-700 text-center mb-8 max-w-2xl mx-auto">
          Get a free consultation for your pond or lake management needs
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <h3 className="text-xl font-bold text-brand-900 mb-6">Send Us a Message</h3>

            {errors.length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
                <p className="font-semibold text-red-700 mb-2">Please fix the following errors:</p>
                <ul className="list-disc list-inside text-red-600 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-brand-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brand-200 rounded-lg focus:border-water-500 focus:outline-none transition-colors"
                  placeholder="Your full name"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-brand-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brand-200 rounded-lg focus:border-water-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-brand-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brand-200 rounded-lg focus:border-water-500 focus:outline-none transition-colors"
                  placeholder="(555) 555-5555"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="service_type" className="block text-sm font-medium text-brand-700 mb-1">
                  Service Type *
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brand-200 rounded-lg focus:border-water-500 focus:outline-none transition-colors bg-white"
                  disabled={isSubmitting}
                >
                  {serviceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-brand-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-brand-200 rounded-lg focus:border-water-500 focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your pond or lake and how we can help..."
                  disabled={isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-nature-600 hover:bg-nature-700 disabled:bg-slate-400 text-white font-bold py-4 px-8 rounded-lg transition-colors duration-200 min-h-[56px] flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
            </form>
          </div>

          {/* Contact Info Card */}
          <div className="space-y-6">
            {/* Direct Contact */}
            <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 border-2 border-brand-200">
              <h3 className="text-xl font-bold text-brand-900 mb-4">
                Or Contact Joe Directly
              </h3>
              <p className="text-brand-700 mb-6">
                Prefer to talk? Joe is available to discuss your needs and provide a free consultation.
              </p>

              <div className="space-y-4">
                <a
                  href="tel:+17272773366"
                  className="flex items-center justify-center bg-water-600 hover:bg-water-700 text-white font-bold py-4 px-6 rounded-lg transition-colors min-h-[56px] w-full"
                >
                  <span className="mr-2 text-xl">📞</span>
                  Call: (727) 277-3366
                </a>

                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center justify-center bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 px-6 rounded-lg transition-colors min-h-[56px] w-full"
                >
                  <span className="mr-2 text-xl">✉️</span>
                  andersponds@gmail.com
                </a>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-br from-water-50 to-brand-50 rounded-lg p-6 border-2 border-water-200">
              <h3 className="text-lg font-bold text-brand-900 mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-nature-600 mr-2">✓</span>
                  <span className="text-brand-700">Licensed by Florida Department of Agriculture</span>
                </li>
                <li className="flex items-start">
                  <span className="text-nature-600 mr-2">✓</span>
                  <span className="text-brand-700">Fully insured for your protection</span>
                </li>
                <li className="flex items-start">
                  <span className="text-nature-600 mr-2">✓</span>
                  <span className="text-brand-700">Free consultations and estimates</span>
                </li>
                <li className="flex items-start">
                  <span className="text-nature-600 mr-2">✓</span>
                  <span className="text-brand-700">Serving Greater Tampa Bay and St. Pete for years</span>
                </li>
              </ul>
            </div>

            {/* Response Time */}
            <div className="bg-white rounded-lg p-4 text-center border border-brand-200">
              <p className="text-brand-700">
                <span className="font-semibold">Typical response time:</span> Within 24 hours
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactForm;
